/**
 * A2P 10DLC wizard (TELEPHONY.md Flow 5). Five steps:
 *
 *   1) Business profile — collect / confirm legal name, EIN, address,
 *      website, business type.
 *   2) Brand submission — picks brand type, captures authorised rep
 *      email, fires the carrier submission. Surfaces the OTP step
 *      Twilio drives.
 *   3) OTP verification — admin types the SMS code Twilio sent.
 *   4) Campaign — use case + sample messages + opt-in / opt-out / help
 *      message. BDC sample templates pre-fill.
 *   5) Done — shows current status with a Refresh button.
 *
 * The wizard reads `a2pStatus` to compute the right starting step
 * when the dialog re-opens later (e.g. admin closes mid-flow).
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@lumika/ui";
import { z } from "zod";
import {
  A2pBrandTypeSchema,
  A2pJobPositionSchema,
  A2pSubmitCampaignSchema,
  COUNTRIES,
  EinSchema,
  TELEPHONY_PRICING,
  TelephonyBusinessTypeSchema,
  US_STATES,
  UsZipSchema,
  formatUsd,
} from "@lumika/shared";
import type {
  A2pBrandType,
  A2pJobPosition,
  A2pStatus,
  A2pSubmitCampaignDto,
  TelephonyAccount,
  TelephonyBusinessType,
} from "@lumika/shared";
import { adminApi } from "../../api/admin";
import { BUSINESS_TYPE_LABELS } from "./DealershipTelephonyPage";

const BRAND_LABELS: Record<TelephonyAccount["brandStatus"], string> = {
  NOT_STARTED: "Not started",
  DRAFT: "Draft",
  PENDING_OTP: "Awaiting OTP",
  IN_REVIEW: "In review",
  APPROVED: "Approved",
  FAILED: "Failed",
};

// Labels for the shared A2pBrandTypeSchema enum — the vocabulary
// itself comes from the schema, never a local copy.
const BRAND_TYPE_LABELS: Record<A2pBrandType, string> = {
  STANDARD: "Standard",
  SOLE_PROPRIETOR: "Sole proprietor",
  LOW_VOLUME_STANDARD: "Low-volume Standard (cheaper, lower throughput)",
};

const USE_CASE_OPTIONS: Array<{ value: A2pSubmitCampaignDto["useCase"]; label: string }> = [
  { value: "CUSTOMER_CARE", label: "Customer Care (service reminders, RO updates)" },
  { value: "MARKETING", label: "Marketing (promotions, new-inventory blasts)" },
  { value: "MIXED", label: "Mixed (customer care + marketing)" },
  { value: "LOW_VOLUME", label: "Low volume (under 6,000 segs/day)" },
  { value: "ACCOUNT_NOTIFICATION", label: "Account notifications" },
  { value: "DELIVERY_NOTIFICATION", label: "Delivery notifications" },
];

const BDC_SAMPLES = [
  "Hi {customer_first_name}, this is {rooftop_dba_name}. Your {vehicle_year} {vehicle_make} is due for service. Reply YES to schedule or call us at {rooftop_phone}. Reply STOP to unsubscribe.",
  "Hi {customer_first_name}, {rooftop_dba_name} confirming your service appointment on {appt_date} at {appt_time}. Reply C to confirm, R to reschedule, or STOP to unsubscribe.",
  "Hi {customer_first_name}, your vehicle is ready for pickup at {rooftop_dba_name}. We're open until {close_time} today. Reply STOP to unsubscribe.",
  "Hi {customer_first_name}, thanks for your interest in the {vehicle_year} {vehicle_make} {vehicle_model} at {rooftop_dba_name}. I'd love to set up a test drive. Reply or call {rooftop_phone}. Reply STOP to unsubscribe.",
  "Hi {customer_first_name}, an update on your repair order at {rooftop_dba_name}: {ro_status_message}. Questions? Call {rooftop_phone}. Reply STOP to unsubscribe.",
];

const DEFAULT_MESSAGE_FLOW =
  "Customers opt in to receive SMS notifications from {rooftop_dba_name} by signing a Repair Order at our service drive, submitting a lead form on our website, or providing verbal consent during a phone call. All consent is logged in our CRM.";

/**
 * Estimated Twilio A2P 10DLC fees (US public carrier rate card, May
 * 2026). Shown during onboarding so the admin sees the cost before
 * submitting a brand or campaign. These are Twilio/TCR pass-through
 * charges — the exact amount Twilio bills lands on the billing
 * dashboard via the nightly usage sync, so this is an estimate, not
 * the invoiced figure.
 */
// Carrier fees come from the shared pricing table so the dealer app
// and this admin wizard always quote the same numbers.
const A2P_FEES = {
  brandRegistration: TELEPHONY_PRICING.a2pBrandRegistration,
  standardVetting: TELEPHONY_PRICING.a2pStandardVetting,
  campaignRegistration: TELEPHONY_PRICING.a2pCampaignRegistration,
  campaignMonthlyStandard: TELEPHONY_PRICING.a2pCampaignMonthlyStandard,
  campaignMonthlyLowVolume: TELEPHONY_PRICING.a2pCampaignMonthlyLowVolume,
} as const;

const fmtUsd = formatUsd;

type Step = "profile" | "brand" | "otp" | "campaign" | "done";

/**
 * Which wizard step a server A2P status maps to. A FAILED brand routes
 * back to the brand step so it can be resubmitted; a PENDING_OTP brand
 * to the SMS-verification step.
 */
function stepForStatus(s: A2pStatus): Step {
  if (s.campaignStatus !== "DRAFT") return "done";
  if (s.brandStatus === "APPROVED") return "campaign";
  if (s.brandStatus === "IN_REVIEW") return "done";
  if (s.brandStatus === "PENDING_OTP") return "otp";
  if (s.brandStatus === "FAILED") return "brand";
  return "profile";
}

/** Muted callout that discloses the estimated carrier fee for a step. */
function FeeCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
      {children}
    </div>
  );
}

export function A2pWizardDialog({
  account,
  onClose,
}: {
  account: TelephonyAccount;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const accountId = account.id;
  const statusQ = useQuery({
    queryKey: ["a2p-status", accountId],
    queryFn: () => adminApi.a2pStatus(accountId),
  });
  const [step, setStep] = useState<Step>("profile");
  // Seed the wizard at the right step when the dialog first reads
  // server state. After that the user drives the step manually.
  useEffect(() => {
    if (statusQ.data) setStep(stepForStatus(statusQ.data));
    // run once when status arrives
  }, [Boolean(statusQ.data)]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" preventDismiss>
        <DialogHeader>
          <DialogTitle>SMS brand & campaign — {account.displayName}</DialogTitle>
        </DialogHeader>
        {/* Steps render only after the initial status resolves, so the
            seeding effect above never yanks a step out from under the
            operator; an error never silently masquerades as NOT_STARTED. */}
        {statusQ.isPending ? (
          <PageLoading message="Loading A2P status…" />
        ) : statusQ.isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <p>Couldn&rsquo;t load the A2P status — {statusQ.error.message}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => statusQ.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
            <ProgressBar
              step={step}
              status={statusQ.data ?? null}
              onJump={(s) => setStep(s)}
            />
            {step === "profile" && (
              <ProfileStep
                account={account}
                onNext={() => setStep("brand")}
              />
            )}
            {step === "brand" && (
              <BrandStep
                accountId={accountId}
                onSubmitted={(needsOtp) => {
                  qc.invalidateQueries({ queryKey: ["a2p-status", accountId] });
                  // Only SOLE_PROPRIETOR brands have an SMS OTP step;
                  // STANDARD / LOW_VOLUME_STANDARD go straight to review.
                  setStep(needsOtp ? "otp" : "done");
                }}
              />
            )}
            {step === "otp" && (
              <OtpStep
                accountId={accountId}
                onResolved={(brandStatus) => {
                  qc.invalidateQueries({ queryKey: ["a2p-status", accountId] });
                  setStep(
                    brandStatus === "APPROVED"
                      ? "campaign"
                      : brandStatus === "FAILED"
                        ? "brand"
                        : "done",
                  );
                }}
              />
            )}
            {step === "campaign" && (
              <CampaignStep
                accountId={accountId}
                onSubmitted={() => {
                  qc.invalidateQueries({ queryKey: ["a2p-status", accountId] });
                  setStep("done");
                }}
              />
            )}
            {step === "done" && (
              <DoneStep
                accountId={accountId}
                status={statusQ.data ?? null}
                onRefreshed={() => qc.invalidateQueries({ queryKey: ["a2p-status", accountId] })}
                onClose={onClose}
                onGoCampaign={() => setStep("campaign")}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProgressBar({
  step,
  status,
  onJump,
}: {
  step: Step;
  status: A2pStatus | null;
  onJump: (s: Step) => void;
}) {
  // Profile and Brand stay reachable (re-edit / resubmit are legitimate);
  // forward steps unlock only once the server status allows them. While
  // status is null every tile stays enabled so a failed fetch never locks
  // navigation.
  const tiles: Array<{ key: Step; label: string; done: boolean; enabled: boolean; hint?: string }> = [
    {
      key: "profile",
      label: "Profile",
      done: step !== "profile" || (status?.brandStatus ?? "NOT_STARTED") !== "NOT_STARTED",
      enabled: true,
    },
    {
      key: "brand",
      label: "Brand",
      done: ["IN_REVIEW", "APPROVED", "FAILED"].includes(status?.brandStatus ?? ""),
      enabled: true,
    },
    {
      key: "otp",
      label: "OTP",
      done: ["IN_REVIEW", "APPROVED", "FAILED"].includes(status?.brandStatus ?? ""),
      enabled: !status || status.brandStatus === "PENDING_OTP",
      hint: "Only available while the brand is awaiting SMS verification.",
    },
    {
      key: "campaign",
      label: "Campaign",
      done: status?.campaignStatus !== undefined && status.campaignStatus !== "DRAFT",
      enabled: !status || status.brandStatus === "APPROVED",
      hint: "Brand must be approved first.",
    },
  ];
  return (
    <ol className="flex flex-wrap gap-2 text-xs">
      {tiles.map((t) => (
        <li key={t.key}>
          <button
            type="button"
            onClick={() => onJump(t.key)}
            disabled={!t.enabled}
            title={t.enabled ? undefined : t.hint}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50 ${
              step === t.key
                ? "border-foreground bg-foreground text-background"
                : t.done
                  ? "border-border bg-muted/40 text-foreground"
                  : "border-border text-muted-foreground"
            }`}
          >
            {t.done && <CheckCircle2 className="h-3 w-3" />}
            {t.label}
          </button>
        </li>
      ))}
    </ol>
  );
}

function ProfileStep({
  account,
  onNext,
}: {
  account: TelephonyAccount;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState({
    legalBusinessName: account.legalBusinessName ?? "",
    ein: "",
    businessAddressLine1: account.businessAddressLine1 ?? "",
    businessAddressLine2: account.businessAddressLine2 ?? "",
    businessCity: account.businessCity ?? "",
    businessState: account.businessState ?? "",
    businessPostalCode: account.businessPostalCode ?? "",
    businessCountry: account.businessCountry || "US",
    websiteUrl: account.websiteUrl ?? "",
    businessType: (account.businessType ?? "") as TelephonyBusinessType | "",
  });
  const [errors, setErrors] = useState<{ ein?: string; websiteUrl?: string; businessPostalCode?: string }>({});
  const set = <K extends keyof typeof values>(k: K, v: (typeof values)[K]) =>
    setValues((cur) => ({ ...cur, [k]: v }));

  // Mirror the server schemas: EIN, website, and ZIP are optional but,
  // when present, must match their canonical format before we submit.
  const validate = () => {
    const next: typeof errors = {};
    if (values.ein) {
      const r = EinSchema.safeParse(values.ein);
      if (!r.success) next.ein = r.error.issues[0]?.message;
    }
    if (values.websiteUrl) {
      const r = z.string().url("Use a full URL, e.g. https://example.com.").safeParse(values.websiteUrl);
      if (!r.success) next.websiteUrl = r.error.issues[0]?.message;
    }
    if (values.businessPostalCode) {
      const r = UsZipSchema.safeParse(values.businessPostalCode);
      if (!r.success) next.businessPostalCode = r.error.issues[0]?.message;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = useMutation({
    mutationFn: () =>
      adminApi.a2pSaveBusinessProfile(account.id, {
        legalBusinessName: values.legalBusinessName || null,
        ein: values.ein ? values.ein : null,
        businessAddressLine1: values.businessAddressLine1 || null,
        businessAddressLine2: values.businessAddressLine2 || null,
        businessCity: values.businessCity || null,
        businessState: values.businessState || null,
        businessPostalCode: values.businessPostalCode || null,
        businessCountry: values.businessCountry || "US",
        websiteUrl: values.websiteUrl || null,
        businessType: values.businessType || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "telephony-account", account.id] });
      toast.success("Business profile saved.");
      onNext();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Section
      title="Business profile"
      description="Carrier-mandated identity for the legal entity behind this Telephony Account."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Legal business name">
          <Input
            value={values.legalBusinessName}
            onChange={(e) => set("legalBusinessName", e.target.value)}
          />
        </FormField>
        <FormField label="EIN" description="Format: 12-3456789" error={errors.ein}>
          <Input value={values.ein} onChange={(e) => set("ein", e.target.value)} placeholder={account.einLast4 ? `••-•••${account.einLast4}` : "12-3456789"} />
        </FormField>
        <FormField label="Business type">
          <Select
            value={values.businessType === "" ? undefined : values.businessType}
            onValueChange={(v) => set("businessType", v as TelephonyBusinessType)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {TelephonyBusinessTypeSchema.options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {BUSINESS_TYPE_LABELS[opt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Website" error={errors.websiteUrl}>
          <Input value={values.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)} placeholder="https://" />
        </FormField>
        <FormField label="Address line 1" className="md:col-span-2">
          <Input value={values.businessAddressLine1} onChange={(e) => set("businessAddressLine1", e.target.value)} />
        </FormField>
        <FormField label="Address line 2" className="md:col-span-2">
          <Input value={values.businessAddressLine2} onChange={(e) => set("businessAddressLine2", e.target.value)} />
        </FormField>
        <FormField label="City">
          <Input value={values.businessCity} onChange={(e) => set("businessCity", e.target.value)} />
        </FormField>
        <FormField label="State">
          <Select
            value={values.businessState === "" ? undefined : values.businessState}
            onValueChange={(v) => set("businessState", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a state…" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Postal code" error={errors.businessPostalCode}>
          <Input value={values.businessPostalCode} onChange={(e) => set("businessPostalCode", e.target.value)} />
        </FormField>
        <FormField label="Country">
          <Select
            value={values.businessCountry}
            onValueChange={(v) => set("businessCountry", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <DialogFooter>
        <Button onClick={() => validate() && save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save & continue"}
        </Button>
      </DialogFooter>
    </Section>
  );
}

function BrandStep({
  accountId,
  onSubmitted,
}: {
  accountId: string;
  onSubmitted: (needsOtp: boolean) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [authorizedRepEmail, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessTitle, setBusinessTitle] = useState("");
  const [jobPosition, setJobPosition] = useState<A2pJobPosition>("CEO");
  const [brandType, setBrandType] = useState<A2pBrandType>("STANDARD");
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    businessTitle?: string;
  }>({});

  const submit = useMutation({
    mutationFn: () =>
      adminApi.a2pSubmitBrand(accountId, {
        authorizedRepFirstName: firstName.trim(),
        authorizedRepLastName: lastName.trim(),
        authorizedRepEmail: authorizedRepEmail.trim(),
        authorizedRepPhone: phone.trim(),
        authorizedRepBusinessTitle: businessTitle.trim(),
        authorizedRepJobPosition: jobPosition,
        brandType,
      }),
    onSuccess: () => {
      const needsOtp = brandType === "SOLE_PROPRIETOR";
      toast.success(
        needsOtp
          ? "Brand submitted. Watch the rep's phone for the OTP."
          : "Brand submitted — now under carrier review.",
      );
      onSubmitted(needsOtp);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const phoneValid = /^\+[1-9]\d{6,14}$/.test(phone.trim());
  const emailValid = z.string().email().safeParse(authorizedRepEmail.trim()).success;

  // Validate on click (mirrors ProfileStep) so the operator sees which
  // field blocks submission instead of a silently disabled button.
  const validate = () => {
    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = "Required.";
    if (!lastName.trim()) next.lastName = "Required.";
    if (!emailValid) next.email = "Enter a valid email.";
    if (!phoneValid) next.phone = "Use E.164 format, e.g. +15551234567.";
    if (!businessTitle.trim()) next.businessTitle = "Required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Standard brands carry a mandatory one-time external vetting fee on
  // top of the flat brand-registration fee; the cheaper brand types do
  // not. Disclosed below so the admin sees the cost before submitting.
  const brandOneTime =
    brandType === "STANDARD"
      ? A2P_FEES.brandRegistration + A2P_FEES.standardVetting
      : A2P_FEES.brandRegistration;

  return (
    <Section title="Brand registration" description="Submits the Customer Profile + A2P Trust Product + Brand to the carrier.">
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Authorised rep first name" error={errors.firstName}>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </FormField>
        <FormField label="Authorised rep last name" error={errors.lastName}>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </FormField>
        <FormField label="Authorised rep email" description="Used as the contact for the trust hub bundle." error={errors.email}>
          <Input value={authorizedRepEmail} onChange={(e) => setEmail(e.target.value)} type="email" />
        </FormField>
        <FormField label="Authorised rep phone" description="E.164 format, e.g. +15551234567." error={errors.phone}>
          <Input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567" />
        </FormField>
        <FormField label="Business title" error={errors.businessTitle}>
          <Input
            value={businessTitle}
            onChange={(e) => setBusinessTitle(e.target.value)}
            placeholder="e.g. General Manager"
          />
        </FormField>
        <FormField label="Job position">
          <Select value={jobPosition} onValueChange={(v) => setJobPosition(v as A2pJobPosition)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {A2pJobPositionSchema.options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Brand type">
          <Select value={brandType} onValueChange={(v) => setBrandType(v as A2pBrandType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {A2pBrandTypeSchema.options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {BRAND_TYPE_LABELS[opt]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <FeeCallout>
        <span className="font-medium text-foreground">
          Estimated Twilio fee: {fmtUsd(brandOneTime)} one-time.
        </span>{" "}
        {brandType === "STANDARD"
          ? `${fmtUsd(A2P_FEES.brandRegistration)} brand registration + ${fmtUsd(
              A2P_FEES.standardVetting,
            )} mandatory external vetting.`
          : `${fmtUsd(
              A2P_FEES.brandRegistration,
            )} brand registration — this brand type skips external vetting.`}{" "}
        Billed by Twilio/TCR; appears on the dealership&rsquo;s billing dashboard once charged.
      </FeeCallout>
      <DialogFooter>
        <Button onClick={() => validate() && submit.mutate()} disabled={submit.isPending}>
          {submit.isPending ? "Submitting…" : "Submit brand"}
        </Button>
      </DialogFooter>
    </Section>
  );
}

function OtpStep({
  accountId,
  onResolved,
}: {
  accountId: string;
  onResolved: (brandStatus: string) => void;
}) {
  // The brand may not actually be awaiting OTP — a STANDARD brand
  // whose status went stale, or one that already failed carrier
  // review. Re-sync from Twilio on mount; if it moved past
  // PENDING_OTP, leave this step at once (self-heals a wizard wrongly
  // parked here).
  const sync = useMutation({
    mutationFn: () => adminApi.a2pRefreshBrand(accountId),
    onSuccess: (acct) => {
      if (acct.brandStatus !== "PENDING_OTP") onResolved(acct.brandStatus);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const resend = useMutation({
    mutationFn: () => adminApi.a2pResendOtp(accountId),
    onSuccess: () => toast.success("Verification text re-sent to the rep's phone."),
    onError: (err: Error) => toast.error(err.message),
  });
  // Mount-once: re-sync the brand status from Twilio.
  useEffect(() => void sync.mutate(), []);

  return (
    <Section
      title="Verify the brand by SMS"
      description="Sole-proprietor brands are confirmed by a text message."
    >
      <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        Twilio texted the authorized representative&rsquo;s mobile from a US
        number. They must{" "}
        <span className="font-medium text-foreground">reply YES</span> to that
        message to verify the brand — there is no code to enter here. It can take
        a few minutes to process after they reply; use{" "}
        <span className="font-medium text-foreground">Check status</span> once
        they have.
      </div>
      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
        >
          {resend.isPending ? "Re-sending…" : "Re-send the text"}
        </Button>
        <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
          {sync.isPending ? "Checking…" : "Check status"}
        </Button>
      </DialogFooter>
    </Section>
  );
}

function CampaignStep({
  accountId,
  onSubmitted,
}: {
  accountId: string;
  onSubmitted: () => void;
}) {
  const [useCase, setUseCase] = useState<A2pSubmitCampaignDto["useCase"]>("CUSTOMER_CARE");
  const [description, setDescription] = useState(
    "Service reminders, appointment confirmations, repair-order updates, and lead follow-up SMS for an automotive dealership BDC.",
  );
  const [samples, setSamples] = useState<string[]>([BDC_SAMPLES[0]!, BDC_SAMPLES[1]!]);
  const [messageFlow, setMessageFlow] = useState(DEFAULT_MESSAGE_FLOW);
  const [errors, setErrors] = useState<{
    description?: string;
    messageSamples?: string;
    messageFlow?: string;
  }>({});

  const toggleSample = (s: string) => {
    setSamples((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : cur.length >= 5 ? cur : [...cur, s],
    );
  };

  const submit = useMutation({
    mutationFn: (dto: A2pSubmitCampaignDto) => adminApi.a2pSubmitCampaign(accountId, dto),
    onSuccess: (data) => {
      toast.success(`Campaign submitted (status ${data.status}).`);
      onSubmitted();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Validate against the SAME schema the backend enforces, on click
  // (mirrors BrandStep/ProfileStep) so the operator sees which field
  // blocks submission instead of a silently disabled button.
  const validate = (): A2pSubmitCampaignDto | null => {
    const result = A2pSubmitCampaignSchema.safeParse({
      useCase,
      description,
      messageSamples: samples,
      messageFlow,
      hasEmbeddedLinks: false,
      // TCR compares samples against the declared attributes: several
      // BDC templates render a real phone number via {rooftop_phone},
      // so declare the flag from the actual selection.
      hasEmbeddedPhone: samples.some((s) => s.includes("{rooftop_phone}")),
    });
    if (!result.success) {
      const next: typeof errors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof typeof errors | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return null;
    }
    setErrors({});
    return result.data;
  };

  // The recurring carrier fee depends on the use case — low-volume
  // campaigns are billed at a lower monthly rate than standard ones.
  const campaignMonthly =
    useCase === "LOW_VOLUME"
      ? A2P_FEES.campaignMonthlyLowVolume
      : A2P_FEES.campaignMonthlyStandard;

  return (
    <Section title="SMS campaign" description="Use case + sample messages the carrier reviews before allowing high-volume SMS.">
      <div className="grid gap-4">
        <FormField label="Use case">
          <Select value={useCase} onValueChange={(v) => setUseCase(v as typeof useCase)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USE_CASE_OPTIONS.map((u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Campaign description (≥40 chars)" error={errors.description}>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={1024}
          />
        </FormField>
        <FormField label="Sample messages" description="Pick 2–5 BDC templates. Interpolation placeholders are read as literal text by the carrier — that's expected." error={errors.messageSamples}>
          <div className="grid gap-2">
            {BDC_SAMPLES.map((s) => (
              <label key={s} className="flex items-start gap-2 rounded-md border border-border p-2 text-xs">
                <input
                  type="checkbox"
                  checked={samples.includes(s)}
                  onChange={() => toggleSample(s)}
                  className="mt-0.5"
                />
                <span>{s}</span>
              </label>
            ))}
          </div>
        </FormField>
        <FormField label="Opt-in description (≥40 chars)" error={errors.messageFlow}>
          <Textarea
            value={messageFlow}
            onChange={(e) => setMessageFlow(e.target.value)}
            rows={4}
            maxLength={2048}
          />
        </FormField>
      </div>
      <FeeCallout>
        <span className="font-medium text-foreground">
          Estimated Twilio fee: {fmtUsd(A2P_FEES.campaignRegistration)} one-time +{" "}
          {fmtUsd(campaignMonthly)}/month.
        </span>{" "}
        One-time campaign vetting plus the recurring carrier fee for this use case. Billed by
        Twilio/TCR; appears on the dealership&rsquo;s billing dashboard once charged.
      </FeeCallout>
      <DialogFooter>
        <Button
          onClick={() => {
            const dto = validate();
            if (dto) submit.mutate(dto);
          }}
          disabled={submit.isPending}
        >
          {submit.isPending ? "Submitting…" : "Submit campaign"}
        </Button>
      </DialogFooter>
    </Section>
  );
}

function DoneStep({
  accountId,
  status,
  onRefreshed,
  onClose,
  onGoCampaign,
}: {
  accountId: string;
  status: A2pStatus | null;
  onRefreshed: () => void;
  onClose: () => void;
  onGoCampaign: () => void;
}) {
  const refresh = useMutation({
    mutationFn: () => adminApi.a2pRefreshBrand(accountId),
    onSuccess: () => {
      toast.success("Status refreshed.");
      onRefreshed();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const reasons = useMemo(
    () => [...(status?.brandFailureReasons ?? []), ...(status?.campaignFailureReasons ?? [])],
    [status],
  );
  return (
    <Section title="A2P status">
      <div className="grid gap-3 text-sm">
        <Row label="Brand">
          {status ? (
            <Badge
              variant={
                status.brandStatus === "APPROVED"
                  ? "success"
                  : status.brandStatus === "FAILED"
                    ? "destructive"
                    : "warning"
              }
            >
              {BRAND_LABELS[status.brandStatus]}
            </Badge>
          ) : (
            "—"
          )}
        </Row>
        <Row label="Trust score">{status?.brandTrustScore ?? "—"}</Row>
        <Row label="Messaging Service">
          {status?.hasMessagingService ? <Badge variant="success">Created</Badge> : "Not yet"}
        </Row>
        <Row label="Campaign">
          {status?.campaignStatus ? (
            <Badge
              variant={
                status.campaignStatus === "VERIFIED"
                  ? "success"
                  : status.campaignStatus === "FAILED"
                    ? "destructive"
                    : "warning"
              }
            >
              {status.campaignStatus}
            </Badge>
          ) : (
            "—"
          )}
        </Row>
        {reasons.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <div className="mb-1 font-medium">Carrier feedback</div>
            <ul className="ml-4 list-disc">
              {reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw className="h-4 w-4" /> {refresh.isPending ? "Refreshing…" : "Refresh"}
        </Button>
        {status?.brandStatus === "APPROVED" && status.campaignStatus === "DRAFT" && (
          <Button onClick={onGoCampaign}>Set up campaign</Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-40 text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}
